/**
 * Date/time formatting utilities for Buenos Aires timezone (UTC-3).
 * All datetime fields are displayed in 24h format with America/Argentina/Buenos_Aires timezone.
 * Date-only fields (birth_date, start_date, end_date) do NOT apply timezone
 * to avoid day-shifting issues (e.g. July 15 UTC -> July 14 in BA).
 */

export const BA_TZ = 'America/Argentina/Buenos_Aires';

/**
 * Format a full ISO datetime string into BA timezone, 24h format.
 * Use for: starts_at, ends_at, measured_at, created_at, paid_at, logged_at, attended_at
 */
export function formatDateTime(isoStr, opts = {}) {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('es-AR', {
        timeZone: BA_TZ,
        hour12: false,
        ...opts,
    });
}

/**
 * Format a full ISO datetime into date + short time (e.g. "15/07/2026, 14:30").
 */
export function formatDateTimeShort(isoStr) {
    return formatDateTime(isoStr, { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Format just the time portion of an ISO datetime, 24h, BA timezone.
 */
export function formatTime(isoStr, opts = {}) {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleTimeString('es-AR', {
        timeZone: BA_TZ,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        ...opts,
    });
}

/**
 * Format a date-only string (YYYY-MM-DD) into dd/mm/yyyy.
 * Also safely handles ISO datetime strings (YYYY-MM-DDTHH:mm:ss...).
 * NO timezone conversion applied — prevents off-by-one day shifts.
 * Use for: birth_date, start_date, end_date
 */
export function formatDate(dateStr) {
    if (!dateStr) return '-';
    // Extract YYYY-MM-DD part if it's an ISO datetime
    const datePart = dateStr.split('T')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d || isNaN(y) || isNaN(m) || isNaN(d)) return '-';
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

/**
 * Extract the date part from an ISO datetime and format as dd/mm/yyyy.
 * Safe for datetime fields that were previously shown as date-only.
 */
export function formatDateFromIso(isoStr) {
    if (!isoStr) return '-';
    const datePart = isoStr.split('T')[0];
    return formatDate(datePart);
}
